/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { When } from "react-if";
import {
  Table,
  Switch,
  Input,
  Text,
  theme,
  SVGIcon,
  FlexContainer,
} from "@nordcloud/gnui";
import { EnvCostShare } from "~/generated/graphql";
import { isNotEmpty, isNotNil, Maybe } from "~/tools";
import {
  ApplicationOption,
  EnvironmentOption,
} from "~/views/estate/components/types";
import {
  InputWrap,
  StyledTd,
  StyledTr,
  SwitchWrap,
  TdWithMinWidth,
} from "./styles";
import { EnvSelectorRowProps, CostMappingAlgorithm } from "./types";

export function EnvSelectorRow({
  name,
  environments,
  algorithm,
  applications,
  envs,
  setEnvs,
  nid,
}: EnvSelectorRowProps) {
  const [isRowOpen, setRowOpen] = React.useState(false);

  const defaultEnv =
    applications?.find((a: ApplicationOption) => a.name === "default")
      ?.environments?.[0]?.id || "";

  const calculateDefaultSpends = (env: EnvCostShare[]) =>
    100 -
    env
      .filter((e: EnvCostShare) => e?.id !== defaultEnv)
      .reduce(
        (sum: number, e: EnvCostShare) => sum + Number(e.splitValue || "0"),
        0
      );

  const defaultEnvIndex = envs.findIndex(
    (e: EnvCostShare) => e?.id === defaultEnv
  );

  const changeValueManual = (id: string, value: number) => {
    const envIndex = envs.findIndex((e: EnvCostShare) => id === e?.id);
    if (envIndex > -1) {
      if (value > 0) {
        setEnvs((prevState: EnvCostShare[]) => {
          const firstSpends = prevState.map(
            (e: EnvCostShare, index: number) => {
              if (index === envIndex) {
                return { ...e, splitValue: value.toString() };
              }
              return e;
            }
          );
          const calculatedSpends = calculateDefaultSpends(firstSpends);
          return firstSpends.map((e: EnvCostShare, index: number) => {
            return index === defaultEnvIndex
              ? { ...e, splitValue: calculatedSpends.toString() }
              : e;
          });
        });
      } else {
        setEnvs((prevState: EnvCostShare[]) => {
          return prevState.filter(
            (_: EnvCostShare, index: number) => index !== envIndex
          );
        });
      }
    } else {
      setEnvs((prevState: EnvCostShare[]) => {
        const defaultRemoved = prevState
          .filter((_, index: number) => index !== defaultEnvIndex)
          .filter((_, index: number) => index !== envIndex);
        return [...defaultRemoved, { id, splitValue: value.toString() }];
      });
    }
  };

  const mappedEnvironments = (environments ?? []).filter(isNotNil);

  const selectedEnvs = mappedEnvironments.filter(
    (mappedEnvironment) =>
      envs.find((env) => env.id === mappedEnvironment.id) != null
  );

  const appSelected = selectedEnvs.length === mappedEnvironments.length;

  React.useEffect(() => {
    if (Array.isArray(environments)) {
      environments.forEach((env: Maybe<EnvironmentOption>) => {
        const isTargetEnv = envs.some((e: EnvCostShare) => e?.id === env?.id);
        const isAlgorithmManual = algorithm === CostMappingAlgorithm.Manual;

        const canAssignManually = !appSelected || isAlgorithmManual;

        if (isTargetEnv && canAssignManually) {
          setRowOpen(true);
        }
      });
    }
  }, [environments, envs, appSelected, algorithm]);

  const hasSelected = isNotEmpty(selectedEnvs);

  const changeValue = (id: string) => {
    const envIndex = envs.findIndex((e: EnvCostShare) => id === e?.id);
    if (envIndex > -1) {
      const newEnvs = envs.filter(
        (_: EnvCostShare, index: number) => index !== envIndex
      );
      setEnvs(newEnvs);
    } else {
      const newEnvs = [
        ...envs,
        {
          id,
          splitValue: undefined,
        },
      ];
      setEnvs(newEnvs);
    }
  };

  const switchApp = () => {
    const newEnvs = [...envs];
    mappedEnvironments.forEach((env) => {
      const selectAllEnvs = !appSelected;
      const envIndex = newEnvs.findIndex(
        (e: EnvCostShare) => env?.id === e?.id
      );

      if (!selectAllEnvs && envIndex > -1) {
        newEnvs.splice(envIndex, 1);
        return;
      }

      if (selectAllEnvs && envIndex === -1) {
        newEnvs.push({
          id: env?.id,
          splitValue: undefined,
        });
      }
    });
    setEnvs(newEnvs);
  };

  return (
    <Table.tbody key={nid}>
      <Table.tr>
        <TdWithMinWidth onClick={() => setRowOpen(!isRowOpen)}>
          {environments && environments?.length > 0 && (
            <div css={{ transform: "translateY(5px)" }}>
              <SVGIcon
                name="chevronDown"
                css={{
                  transition: "transform .1s linear",
                  transform: isRowOpen ? "rotate(0deg)" : "rotate(-90deg)",
                }}
              />
            </div>
          )}
        </TdWithMinWidth>
        <Table.td css={{ wordBreak: "break-all" }}>{name}</Table.td>
        <Table.td>
          <FlexContainer
            justifyContent="flex-end"
            css={{ gap: theme.spacing.spacing02 }}
          >
            <Text tag="div" align="right">
              <Text tag="span" size="md" color="success">
                {hasSelected && selectedEnvs?.length}
              </Text>
              <Text tag="span" size="md" color={theme.color.text.text02}>
                {isNotEmpty(selectedEnvs) && "/"}
                {environments?.length}
              </Text>
            </Text>
            <When
              condition={
                algorithm !== CostMappingAlgorithm.Manual &&
                environments?.length !== 0
              }
            >
              <Switch
                data-testid="application-select"
                checked={appSelected}
                onChange={() => switchApp()}
              />
            </When>
          </FlexContainer>
        </Table.td>
      </Table.tr>

      {environments?.map((env: Maybe<EnvironmentOption>) => (
        <StyledTr key={env?.nid} show={isRowOpen}>
          <td />
          <Table.td css={{ wordBreak: "break-all" }}>{env?.name}</Table.td>
          <StyledTd>
            {algorithm === CostMappingAlgorithm.Manual && (
              <FlexContainer justifyContent="flex-end">
                <InputWrap>
                  <Input
                    small
                    type="text"
                    value={
                      Number(
                        envs.find((e: EnvCostShare) => e?.id === env?.id)
                          ?.splitValue
                      ) || ""
                    }
                    onChange={(e) =>
                      changeValueManual(String(env?.id), Number(e.target.value))
                    }
                  />
                </InputWrap>
                %
              </FlexContainer>
            )}
            {algorithm !== CostMappingAlgorithm.Manual && (
              <SwitchWrap>
                <Switch
                  checked={!!envs.find((e: EnvCostShare) => e?.id === env?.id)}
                  onChange={() => {
                    changeValue(String(env?.id));
                  }}
                />
              </SwitchWrap>
            )}
          </StyledTd>
        </StyledTr>
      ))}
    </Table.tbody>
  );
}
